/**
 * Preparação de imagem para o chat do assistente.
 *
 * Um print de tela de monitor 4K passa fácil de 5 MB; o modelo não precisa de
 * nada disso e o backend limita o upload. Reduzimos no cliente: o lado maior
 * cai para `LADO_MAX` px (o que os modelos de visão costumam usar de qualquer
 * jeito) e a imagem é reencodada. PNG é mantido quando é PNG — texto de print
 * fica ilegível com artefato de JPEG; qualquer outro formato vira PNG.
 */

export const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const LADO_MAX = 1568;

export type ImagemPreparada = {
    blob: Blob;
    /** Data URL para o preview e para o balão otimista da mensagem. */
    dataUrl: string;
    nome: string;
};

export function ehImagemAceita(tipo: string): boolean {
    return TIPOS_ACEITOS.includes(tipo);
}

/** Primeira imagem de um `DataTransfer` (clipboard ou drag-and-drop), se houver. */
export function imagemDoDataTransfer(dt: DataTransfer | null): File | null {
    if (!dt) return null;
    for (const item of Array.from(dt.items ?? [])) {
        if (item.kind === 'file' && ehImagemAceita(item.type)) {
            const f = item.getAsFile();
            if (f) return f;
        }
    }
    for (const f of Array.from(dt.files ?? [])) {
        if (ehImagemAceita(f.type)) return f;
    }
    return null;
}

export async function prepararImagem(arquivo: File): Promise<ImagemPreparada> {
    if (!ehImagemAceita(arquivo.type)) {
        throw new Error('Formato não suportado. Use PNG, JPEG, WebP ou GIF.');
    }

    const bitmap = await createImageBitmap(arquivo);
    try {
        const escala = Math.min(1, LADO_MAX / Math.max(bitmap.width, bitmap.height));
        const tipoSaida = arquivo.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';

        // Já cabe e já está num formato que vamos manter: envia como veio.
        if (escala === 1 && tipoSaida === arquivo.type) {
            return { blob: arquivo, dataUrl: await paraDataUrl(arquivo), nome: arquivo.name };
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * escala);
        canvas.height = Math.round(bitmap.height * escala);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Não foi possível processar a imagem');
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (b) => (b ? resolve(b) : reject(new Error('Não foi possível processar a imagem'))),
                tipoSaida,
                0.9,
            );
        });
        const nome = arquivo.name.replace(/\.[^.]+$/, '') + (tipoSaida === 'image/jpeg' ? '.jpg' : '.png');
        return { blob, dataUrl: await paraDataUrl(blob), nome };
    } finally {
        bitmap.close();
    }
}

function paraDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result));
        leitor.onerror = () => reject(leitor.error ?? new Error('Falha ao ler a imagem'));
        leitor.readAsDataURL(blob);
    });
}
